
import React, { useState } from "react";
import { useTransactionalEmail } from "@/hooks/use-transactional-email";
import { ContactForm } from "@/components/ui/contact-form";
import { toast } from "@/components/ui/use-toast";
import { SuccessModal } from "./SuccessModal";

interface ContactFormHandlerProps {
  nursingHomeName: string;
  nursingHomeId?: string;
  county?: string;
  email?: string;
  className?: string;
  submitLabel?: string;
  redirectUrl?: string;
}

export const ContactFormHandler = ({
  nursingHomeName,
  nursingHomeId,
  county,
  email,
  className,
  submitLabel,
  redirectUrl
}: ContactFormHandlerProps) => {
  const { sendEmail, isSending } = useTransactionalEmail();
  const [formSubmitted, setFormSubmitted] = useState(false);

  const handleSubmit = async (data: {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    message: string;
  }) => {
    try {
      // Format the user information for the nursing home
      const userInfo = `${data.firstName} ${data.lastName}${data.phone ? ` • ${data.phone}` : ''}`;
      
      // Construct the claim link for the nursing home
      const claimLink = `https://www.grandpal.co/claim/${nursingHomeId || ''}`;
      
      // First, send the confirmation email to the user
      const userConfirmationSent = await sendEmail({
        type: 'inquiry_confirmation',
        data: {
          firstName: data.firstName,
          email: data.email,
          nursingHomeName,
          county,
        }
      });
      
      // Then, send the inquiry notification to the nursing home or Grandpal
      // If the nursing home has an email address, send it there, otherwise send to Grandpal
      const inquiryEmailAddress = email || 'hello@updates.grandpal.co';
      
      const nursingHomeInquirySent = await sendEmail({
        type: 'nursing_home_inquiry',
        data: {
          email: inquiryEmailAddress,
          nursingHomeName,
          county: county || 'Ireland',
          message: data.message,
          userInfo,
          claimLink,
        }
      });
      
      if (userConfirmationSent && nursingHomeInquirySent) {
        // Instead of showing toast and redirecting, set formSubmitted to true
        setFormSubmitted(true);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error("Error sending inquiry emails:", error);
      // Update the toast call to use the sonner format
      toast("Failed to send your inquiry. Please try again or contact us directly.");
      return false;
    }
  };

  // If form has been submitted, show the success modal instead of the contact form
  if (formSubmitted) {
    return <SuccessModal nursingHomeName={nursingHomeName} />;
  }

  return (
    <ContactForm 
      className={className}
      submitLabel={submitLabel || "Send Inquiry"}
      redirectUrl={redirectUrl}
      onSubmit={handleSubmit}
      isSubmitting={isSending}
    />
  );
};
